import React, { useState } from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { Button, Input } from '@material-tailwind/react';
import { Option } from '@material-tailwind/react';
import clsx from 'clsx';
import { ReactComponent as ErrorIcon } from '@assets/icons/info_error_light.svg';
import Select from '@components/forms/Select';
import ConfirmSellerModal from '@components/widgets/ConfirmSellerModal';
import LoadingComponent from '@components/widgets/LoadingComponent';
import _find from 'lodash/find';
import { useSellerStore } from '@stores';
import {
  CardCaptureStepEnum,
  DocumentCaptureStepEnum
} from '@types';
import {
  GET_INSPECTION_CHECK,
  SET_INSPECTION_DOCUMENT,
  post
} from '@services';
import { getBankReviewDefaults } from './bankReviewDefaults';
import { recordAPIError } from '@utils/rumMonitoring';

interface CaptureReviewProps {
  type: string;
  primaryColor: string;
  setStep: (step: number) => void;
}

const CaptureReview: React.FC<CaptureReviewProps> = ({
  type,
  primaryColor,
  setStep
}) => {
  const {
    vinObj,
    numberPlate,
    registration,
    bank,
    operatingHours,
    marineHin,
    marineRegoNumber,
    setNumberPlate,
    setVin,
    setBank,
    inspectionId,
    assetType,
    setInspectionChecks,
    setBankFormStatus,
    setVinFormStatus,
    setNumberPlateFormStatus,
    setOperatingHours,
    setMarineHin,
    setMarineRegoNumber,
    setMarineHinFormStatus,
    setMarineRegoNumberFormStatus,
    setMarineHinImage,
    setMarineRegoNumberImage
  } = useSellerStore();

  const vinRegExp = /^[A-HJ-NPR-Z0-9]{17}$/;

  const recognizeForm = yup
    .object()
    .shape({
      type: yup.string().required(),
      vinValue: yup.string().when('type', {
        is: (value: string) => value === CardCaptureStepEnum.vin,
        then: (schema) =>
          schema.required('Please enter this field').matches(vinRegExp, {
            message: (
              <span>
                The VIN <span className="font-bold">must be 17 characters</span>{' '}
                long and can only include the following characters: 0–9, A–Z
                (uppercase){' '}
                <span className="font-bold">excluding the letters</span> I, O
                and Q
              </span>
            )
          }),
        otherwise: (schema) => schema.nullable()
      }),
      numberPlateValue: yup.string().when('type', {
        is: (value: string) => value === CardCaptureStepEnum.plateNumber,
        then: (schema) =>
          schema
            .required('Please enter the plate number.')
            .max(9, 'Registration plate must be 9 or fewer characters'),
        otherwise: (schema) => schema.nullable()
      }),
      stateValue: yup.string().when('type', {
        is: (value: string) => value === CardCaptureStepEnum.plateNumber,
        then: (schema) => schema.required('Please select the state'),
        otherwise: (schema) => schema.nullable()
      }),
      hinValue: yup.string().when('type', {
        is: (value: string) => value === CardCaptureStepEnum.marineHin,
        then: (schema) =>
          schema
            .required('Please enter the HIN')
            .min(12, 'HIN must be 12-14 characters long')
            .max(14, 'HIN must be 12-14 characters long')
            .matches(
              /^[A-Z0-9]+$/,
              'HIN can only contain uppercase letters and numbers'
            ),
        otherwise: (schema) => schema.nullable()
      }),
      marineRegoValue: yup.string().when('type', {
        is: (value: string) => value === CardCaptureStepEnum.marineRegoNumber,
        then: (schema) =>
          schema
            .required('Please enter the registration number')
            .min(1, 'Registration number must be 1-9 characters long')
            .max(9, 'Registration number must be 1-9 characters long')
            .matches(
              /^[A-Z0-9]+$/,
              'Registration number can only contain uppercase letters and numbers'
            ),
        otherwise: (schema) => schema.nullable()
      }),
      addressLine1: yup.string().when('type', {
        is: (value: string) => value === DocumentCaptureStepEnum.bank,
        then: (schema) => schema.trim().required('Please enter Address Line 1'),
        otherwise: (schema) => schema.nullable()
      }),
      addressLine2: yup.string().when('type', {
        is: (value: string) => value === DocumentCaptureStepEnum.bank,
        then: (schema) => schema.trim().required('Please enter Address Line 2'),
        otherwise: (schema) => schema.nullable()
      }),
      bankName: yup.string().when('type', {
        is: (value: string) => value === DocumentCaptureStepEnum.bank,
        then: (schema) => schema.required('Please enter Bank name'),
        otherwise: (schema) => schema.nullable()
      }),
      accountName: yup.string().when('type', {
        is: (value: string) => value === DocumentCaptureStepEnum.bank,
        then: (schema) => schema.required('Please enter Bank account name'),
        otherwise: (schema) => schema.nullable()
      }),
      accountNumber: yup.string().when('type', {
        is: (value: string) => value === DocumentCaptureStepEnum.bank,
        then: (schema) => schema.trim().required('Please enter Account number'),
        otherwise: (schema) => schema.nullable()
      }),
      bsbValue: yup.string().when('type', {
        is: (value: string) => value === DocumentCaptureStepEnum.bank,
        then: (schema) =>
          schema
            .matches(
              /^\d{3}-\d{3}$/,
              'The number must be in the format ***-***.'
            )
            .required('Please enter BSB'),
        otherwise: (schema) => schema.nullable()
      })
    })
    .required();

  const stateOptions = ['NSW', 'QLD', 'SA', 'TAS', 'VIC', 'WA', 'ACT', 'NT'];
  const [submitStatus, setSubmitStatus] = useState(true);

  const {
    register,
    setValue,
    watch,
    trigger,
    clearErrors,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm({
    resolver: yupResolver(recognizeForm),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      type: type,
      vinValue: vinObj.file?.fields2?.vin
        ? vinObj.file?.fields2?.vin?.vin
        : _find(vinObj.ocrFields, { key: 'vin' })?.value || '',
      numberPlateValue: numberPlate.file?.fields2?.licencePlate
        ? numberPlate.file?.fields2.licencePlate.rego
        : _find(numberPlate.ocrFields, { key: 'registrationNumber' })?.value ||
          '',
      stateValue: numberPlate.file?.fields2?.licencePlate
        ? numberPlate.file?.fields2?.licencePlate?.state
        : _find(numberPlate.ocrFields, { key: 'state' })?.value || '',
      hinValue: marineHin.file?.fields2?.marineHin
        ? marineHin.file?.fields2?.marineHin?.hin
        : '',
      marineRegoValue: marineRegoNumber.file?.fields2?.rego
        ? marineRegoNumber.file?.fields2?.marineRegoNumber?.rego
        : '',
      ...getBankReviewDefaults(bank)
    }
  });

  const {
    vinValue,
    numberPlateValue,
    stateValue,
    hinValue,
    marineRegoValue,
    bankName,
    accountName,
    accountNumber,
    bsbValue,
    addressLine1,
    addressLine2
  } = watch();
  const [isOpenModal, setIsOpenModal] = useState(false);
  const formatBSB = (e: any, value: any) => {
    if (value.length > 7) {
      value = value.substring(0, 7);
    }

    const cleaned = value.replace(/\D+/g, '');
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})$/);

    if (!match) return value;

    const [, first, second] = match;
    let formatted = '';

    if (first) formatted += first;
    if (first.length === 3) {
      formatted += second
        ? '-'
        : e.nativeEvent.inputType !== 'deleteContentBackward'
          ? '-'
          : '';
    }
    if (second) formatted += second;

    return formatted;
  };
  const [isLoading, setLoading] = useState(false);
  const onSubmit = (data: any) => {
    let params = {};
    if (type === CardCaptureStepEnum.vin) {
      params = {
        inspectionId: inspectionId,
        documentType: type,
        extraFields: {
          vin: {
            vin: data.vinValue
          }
        }
      };
      setVin({
        ...vinObj,
        file: {
          fields2: {
            vin: {
              vin: data.vinValue
            }
          },
          fileId: vinObj.file?.fileId
        },
        available: true,
        isDone: true
      });
      setVinFormStatus(true);
    } else if (type === CardCaptureStepEnum.plateNumber) {
      params = {
        inspectionId: inspectionId,
        documentType: type,
        extraFields: {
          licencePlate: {
            rego: data.numberPlateValue,
            state: data.stateValue
          }
        }
      };
      setNumberPlate({
        ...numberPlate,
        available: true,
        file: {
          fields2: {
            licencePlate: {
              rego: data.numberPlateValue,
              state: data.stateValue
            }
          },
          fileId: numberPlate.file?.fileId
        },
        isDone: true
      });
      setNumberPlateFormStatus(true);
    } else if (type === CardCaptureStepEnum.marineHin) {
      params = {
        inspectionId: inspectionId,
        documentType: type,
        extraFields: {
          marineHin: {
            hin: data.hinValue
          }
        }
      };
      setMarineHin({
        ...marineHin,
        file: {
          fields2: {
            marineHin: {
              hin: data.hinValue
            }
          },
          fileId: marineHin.file?.fileId
        },
        available: true,
        isDone: true
      });
      setMarineHinFormStatus(true);
    } else if (type === CardCaptureStepEnum.marineRegoNumber) {
      params = {
        inspectionId: inspectionId,
        documentType: type,
        extraFields: {
          marineRegoNumber: {
            rego: data.marineRegoValue
          }
        }
      };
      setMarineRegoNumber({
        ...marineRegoNumber,
        file: {
          fields2: {
            marineRegoNumber: {
              rego: data.marineRegoValue
            }
          },
          fileId: marineRegoNumber.file?.fileId
        },
        available: true,
        isDone: true
      });
      setMarineRegoNumberFormStatus(true);
    } else {
      params = {
        inspectionId: inspectionId,
        documentType: type,
        extraFields: {
          bankStatement: {
            bankName: data.bankName,
            accountName: data.accountName,
            accountNumber: data.accountNumber,
            addressLine1: data.addressLine1,
            addressLine2: data.addressLine2,
            bsb: data.bsbValue
          }
        },
        fileId: bank.file?.fileId
      };
      setBank({
        ...bank,
        file: {
          fields2: {
            bankStatement: {
              bankName: data.bankName,
              accountName: data.accountName,
              accountNumber: data.accountNumber,
              addressLine1: data.addressLine1,
              addressLine2: data.addressLine2,
              bsb: data.bsbValue
            }
          },
          fileId: bank.file?.fileId
        },
        isDone: true
      });
      setBankFormStatus(true);
    }
    setLoading(true);
    post(SET_INSPECTION_DOCUMENT, params, false)
      .then(() => {
        post(GET_INSPECTION_CHECK, { inspectionId: inspectionId }, false)
          .then((data) => {
            if (
              data &&
              data.inspectionChecks &&
              Object.keys(data.inspectionChecks).length !== 0
            ) {
              setInspectionChecks(data.inspectionChecks);
              setLoading(false);
              if (submitStatus) {
                setIsOpenModal(true);
                setSubmitStatus(false);
              } else {
                setStep(4);
              }
            }
          })
          .catch((e) => {
            recordAPIError(e, e.response?.data);
            console.error(e);
          });
      })
      .catch((e) => {
        recordAPIError(e, e.response?.data);
        alert('Failed to re-upload file, Please login again.');
        setLoading(false);
      });
  };

  const handleTextChangeUppercase = (
    event: React.ChangeEvent<HTMLInputElement>,
    fieldName: any
  ) => {
    const upperCaseTrimmedValue = event.target.value
      .replace(/\s+/g, '')
      .toUpperCase();
    setValue(fieldName, upperCaseTrimmedValue, { shouldValidate: true });
  };

  if (isLoading) return <LoadingComponent color={primaryColor} />;

  const reviewButtonTestId = (action: 'confirm') =>
    `seller-capture-review-${type.toLowerCase()}-${action}-button`;

  return (
    <div className="pb-[100px] w-full">
      <div className="gap-6">
        <div className="flex flex-col gap-3">
          <h2 className="text-lg font-semibold text-grey-800">
            {CardCaptureStepEnum.vin === type
              ? 'VIN'
              : CardCaptureStepEnum.plateNumber === type
                ? 'Number Plate'
                : CardCaptureStepEnum.marineHin === type
                  ? 'Hull Identification Number (HIN)'
                  : CardCaptureStepEnum.marineRegoNumber === type
                    ? 'Watercraft Registration Number'
                    : 'Bank Statement'}
          </h2>
          <div className="p-5 border border-green-500 gap-6 flex flex-col rounded-lg">
            <h2 className="text-md font-semibold font-inter text-green-700">
              {CardCaptureStepEnum.vin === type &&
              vinObj.ocrFields &&
              Object.keys(vinObj.ocrFields).length !== 0
                ? 'Have we captured the correct details for the VIN?'
                : CardCaptureStepEnum.vin === type
                  ? 'We were unable to read the image. Please enter the Vehicle Identification Number (VIN) below.'
                  : CardCaptureStepEnum.plateNumber === type &&
                      numberPlate.ocrFields &&
                      Object.keys(numberPlate.ocrFields).length !== 0
                    ? 'Have we captured the correct details for the number plate?'
                    : CardCaptureStepEnum.plateNumber === type
                      ? 'We were unable to read the image. Please enter the Number Plate details below.'
                      : CardCaptureStepEnum.marineHin === type
                        ? 'Please enter the Hull Identification Number (HIN) below.'
                        : CardCaptureStepEnum.marineRegoNumber === type
                          ? 'Please enter the Watercraft Registration Number below.'
                          : 'Please enter the details below.'}
            </h2>

            {CardCaptureStepEnum.vin === type &&
            vinObj.ocrFields &&
            Object.keys(vinObj.ocrFields).length !== 0 ? (
              <p className="text-sm font-regular font-inter text-green-600">
                Please review and edit the text box below to ensure it matches
                your VIN.
              </p>
            ) : CardCaptureStepEnum.plateNumber === type &&
              numberPlate.ocrFields &&
              Object.keys(numberPlate.ocrFields).length !== 0 ? (
              <p className="text-sm font-regular font-inter text-green-600">
                Please review and edit the text boxes below to ensure they
                exactly match your number plate details.
              </p>
            ) : CardCaptureStepEnum.marineHin === type ? (
              <p className="text-sm font-regular font-inter text-green-600">
                Please enter the HIN exactly as it appears on your watercraft.
              </p>
            ) : CardCaptureStepEnum.marineRegoNumber === type ? (
              <p className="text-sm font-regular font-inter text-green-600">
                Please enter the registration number exactly as it appears on
                your marine vessel.
              </p>
            ) : (
              DocumentCaptureStepEnum.bank === type && (
                <p className="text-sm font-regular font-inter text-green-600">
                  Please complete the text boxes below to ensure they exactly
                  match the bank statement provided.
                </p>
              )
            )}
            {type === CardCaptureStepEnum.vin && (
              <>
                <p className="text-sm font-regular font-inter text-green-600">
                  VIN format:
                </p>
                <ul className="list-disc pl-5 text-sm font-regular font-inter text-green-600">
                  <li>
                    A VIN is{' '}
                    <span className="font-bold">always 17 characters</span>{' '}
                    long.
                  </li>
                  <li>
                    A VIN can only include the following characters: 0–9, A–Z
                    (uppercase){' '}
                    <span className="font-bold">excluding the letters</span> I,
                    O and Q. This prevents confusion with visually similar
                    numerals.
                  </li>
                </ul>
              </>
            )}
          </div>
        </div>
        <div className="mt-6 gap-4 flex flex-col">
          {type === CardCaptureStepEnum.vin ? (
            <div className="gap-2 flex flex-col">
              <h3 className="text-sm font-semibold font-inter text-gray-700">
                VIN
              </h3>
              <Input
                placeholder="VIN"
                variant="static"
                className="rounded-none pb-4"
                style={{ borderColor: primaryColor }}
                icon={errors.vinValue && <ErrorIcon />}
                value={vinValue}
                {...register(`vinValue`)}
                onChange={(event) =>
                  handleTextChangeUppercase(event, 'vinValue')
                }
              />
              {errors.vinValue && (
                <span className="text-xs text-error-light">
                  {errors.vinValue.message as string}
                </span>
              )}{' '}
            </div>
          ) : type === CardCaptureStepEnum.plateNumber ? (
            <>
              <div className="gap-2 flex flex-col">
                <h3 className="text-sm font-semibold font-inter text-gray-700">
                  Number Plate
                </h3>
                <div>
                  <Input
                    data-testid="seller-input-numberPlateValue"
                    placeholder=""
                    variant="static"
                    className="rounded-none pb-4"
                    style={{ borderColor: primaryColor }}
                    icon={errors.vinValue && <ErrorIcon />}
                    value={numberPlateValue}
                    {...register(`numberPlateValue`)}
                    onChange={(event) =>
                      handleTextChangeUppercase(event, 'numberPlateValue')
                    }
                  />
                  {errors.numberPlateValue && (
                    <span className="text-xs text-error-light">
                      {errors.numberPlateValue.message as string}
                    </span>
                  )}
                </div>
              </div>
              <div className="gap-3.5 flex flex-col">
                <h3 className="text-sm font-semibold font-inter text-gray-700">
                  Registration State
                </h3>
                <Select
                  placeholder="Select a State"
                  {...register(`stateValue`)}
                  className="custom-state-select dynamic-border"
                  value={stateValue}
                  style={
                    {
                      '--dynamic-border-color': primaryColor
                    } as React.CSSProperties
                  }
                  onChange={(e) => {
                    if (e && e !== '') {
                      clearErrors('stateValue');
                    }
                    setValue('stateValue', e || '');
                  }}>
                  {stateOptions.map((state, index) => (
                    <Option key={index} value={state}>
                      {state}
                    </Option>
                  ))}
                </Select>
                {errors.stateValue && (
                  <span className="text-xs text-error-light">
                    {errors.stateValue.message as string}
                  </span>
                )}
              </div>
            </>
          ) : type === CardCaptureStepEnum.marineHin ? (
            <div className="gap-2 flex flex-col">
              <h3 className="text-sm font-semibold font-inter text-gray-700">
                Hull Identification Number (HIN)
              </h3>
              <Input
                placeholder="HIN"
                variant="static"
                className="rounded-none pb-4"
                style={{ borderColor: primaryColor }}
                icon={errors.hinValue && <ErrorIcon />}
                value={hinValue}
                {...register(`hinValue`)}
                onChange={(event) =>
                  handleTextChangeUppercase(event, 'hinValue')
                }
              />
              {errors.hinValue && (
                <span className="text-xs text-error-light">
                  {errors.hinValue.message as string}
                </span>
              )}
              <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
                <p className="text-sm font-regular font-inter text-blue-600">
                  HIN format:
                </p>
                <ul className="list-disc pl-5 text-sm font-regular font-inter text-blue-600">
                  <li>
                    A HIN is <span className="font-bold">12-14 characters</span>{' '}
                    long.
                  </li>
                  <li>A HIN can only include uppercase letters and numbers.</li>
                </ul>
              </div>
            </div>
          ) : type === CardCaptureStepEnum.marineRegoNumber ? (
            <div className="gap-2 flex flex-col">
              <h3 className="text-sm font-semibold font-inter text-gray-700">
                Registration Number
              </h3>
              <Input
                placeholder="Registration Number"
                variant="static"
                className="rounded-none pb-4"
                style={{ borderColor: primaryColor }}
                icon={errors.marineRegoValue && <ErrorIcon />}
                value={marineRegoValue}
                {...register(`marineRegoValue`)}
                onChange={(event) =>
                  handleTextChangeUppercase(event, 'marineRegoValue')
                }
              />
              {errors.marineRegoValue && (
                <span className="text-xs text-error-light">
                  {errors.marineRegoValue.message as string}
                </span>
              )}
              <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
                <p className="text-sm font-regular font-inter text-blue-600">
                  Registration Number format:
                </p>
                <ul className="list-disc pl-5 text-sm font-regular font-inter text-blue-600">
                  <li>
                    Registration number is{' '}
                    <span className="font-bold">1-9 characters</span> long.
                  </li>
                  <li>Can only include uppercase letters and numbers.</li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="gap-8 flex flex-col pb-[150px]">
              <h3 className="text-sm font-semibold font-inter text-gray-700">
                Bank Statement
              </h3>
              <div>
                <h3 className="text-sm font-semibold font-inter text-gray-700">
                  Bank name
                </h3>
                <Input
                  placeholder="Bank name"
                  variant="static"
                  className="rounded-none pb-4"
                  style={{ borderColor: primaryColor }}
                  value={bankName}
                  {...register(`bankName`)}
                />
                {errors.bankName && (
                  <span className="text-xs text-error-light">
                    {errors.bankName.message as string}
                  </span>
                )}
              </div>
              <div>
                <h3 className="text-sm font-semibold font-inter text-gray-700">
                  Bank account name
                </h3>
                <Input
                  placeholder="Bank account name"
                  variant="static"
                  className="rounded-none pb-4"
                  style={{ borderColor: primaryColor }}
                  value={accountName}
                  {...register(`accountName`)}
                />
                {errors.accountName && (
                  <span className="text-xs text-error-light">
                    {errors.accountName.message as string}
                  </span>
                )}
              </div>
              <div>
                <h3 className="text-sm font-semibold font-inter text-gray-700">
                  BSB
                </h3>
                <Input
                  placeholder="111-000"
                  variant="static"
                  onKeyDown={(e) => {
                    const allowedKeys = [
                      'Backspace',
                      'ArrowLeft',
                      'ArrowRight',
                      'Delete',
                      'Home',
                      'End'
                    ];
                    if (
                      !/^[\d-]+$/.test(e.key) &&
                      !allowedKeys.includes(e.key)
                    ) {
                      e.preventDefault();
                    }
                  }}
                  className="rounded-none pb-4"
                  style={{ borderColor: primaryColor }}
                  value={bsbValue}
                  {...register(`bsbValue`, {
                    onChange: (e) => {
                      const formatted = formatBSB(e, e.target.value);
                      setValue('bsbValue', formatted);
                    }
                  })}
                />
                {errors.bsbValue && (
                  <span className="text-xs text-error-light">
                    {errors.bsbValue.message as string}
                  </span>
                )}
              </div>
              <div>
                <h3 className="text-sm font-semibold font-inter text-gray-700">
                  Account Number
                </h3>
                <Input
                  placeholder="123456789"
                  variant="static"
                  className="rounded-none pb-4"
                  style={{ borderColor: primaryColor }}
                  value={accountNumber}
                  {...register(`accountNumber`)}
                />
                {errors.accountNumber && (
                  <span className="text-xs text-error-light">
                    {errors.accountNumber.message as string}
                  </span>
                )}
              </div>
              <div>
                <h3 className="text-sm font-semibold font-inter text-gray-700">
                  Address Line 1
                </h3>
                <Input
                  placeholder="100 GREEN STREET"
                  variant="static"
                  className="rounded-none pb-4"
                  style={{ borderColor: primaryColor }}
                  value={addressLine1}
                  {...register(`addressLine1`)}
                />
                {errors.addressLine1 && (
                  <span className="text-xs text-error-light">
                    {errors.addressLine1.message as string}
                  </span>
                )}
              </div>
              <div>
                <h3 className="text-sm font-semibold font-inter text-gray-700">
                  Address Line 2
                </h3>
                <Input
                  placeholder="SYDNEY NSW 2000 "
                  variant="static"
                  className="rounded-none pb-4"
                  style={{ borderColor: primaryColor }}
                  value={addressLine2}
                  {...register(`addressLine2`)}
                />
                {errors.addressLine2 && (
                  <span className="text-xs text-error-light">
                    {errors.addressLine2.message as string}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="fixed lg:relative lg:w-full lg:justify-end bottom-0 right-0 w-full px-4 py-6 bg-grey-50 flex flex-col gap-4 tablet-portrait:w-[361px] tablet-portrait:justify-center tablet-portrait:right-auto tablet-portrait:px-0">
        <Button
          data-testid={reviewButtonTestId('confirm')}
          onClick={() => {
            clearErrors();
            if (isValid) {
              if (registration.file && registration.file.fields2) {
                handleSubmit(onSubmit)();
              } else {
                setSubmitStatus(false);
                setIsOpenModal(true);
              }
            } else {
              trigger();
            }
          }}
          className={clsx(
            'font-semibold w-auto normal-case rounded text-base shadow-none py-[10px] px-[18px]',
            primaryColor && `bg-[${primaryColor}]`
          )}
          style={{ backgroundColor: primaryColor }}>
          Confirm
        </Button>
      </div>

      <ConfirmSellerModal
        open={isOpenModal}
        type={
          CardCaptureStepEnum.vin === type
            ? CardCaptureStepEnum.vin
            : type === CardCaptureStepEnum.plateNumber
              ? CardCaptureStepEnum.plateNumber
              : type === CardCaptureStepEnum.marineHin
                ? CardCaptureStepEnum.marineHin
                : type === CardCaptureStepEnum.marineRegoNumber
                  ? CardCaptureStepEnum.marineRegoNumber
                  : DocumentCaptureStepEnum.bank
        }
        vin={type === CardCaptureStepEnum.vin ? vinValue : ''}
        licencePlate={
          type === CardCaptureStepEnum.plateNumber
            ? { rego: numberPlateValue || '', state: stateValue || '' }
            : { rego: '', state: '' }
        }
        onSubmit={handleSubmit(onSubmit)}
        onClose={() => {
          setIsOpenModal(false);
          setSubmitStatus(true);
        }}
        setStep={() => setStep(4)}
      />
    </div>
  );
};

export default CaptureReview;